<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { uuid as newUuid } from '@addictivegroup/addictive-offline'
import { useMeta } from '@/stores/meta.js'
import { getRecord } from '@/lib/records.js'
import { formatAddress } from '@/lib/format.js'
import { addressRoles, resolveRoles, toggleRole } from '@/lib/addressRoles.js'
import AddressInput from './AddressInput.vue'

/**
 * An account's or a contact's addresses (D-198), each playing the roles ticked on it; one address
 * does for everything, a second is added only where a role really is elsewhere. Which address a
 * role nobody ticked takes is said only on hovering it, not written out. A single role ticked
 * on another address moves there. A contact's address may point at one of its account's: shown
 * as that, linked to the account, following it.
 */
const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  entity: { type: String, default: 'account' },
  record: { type: Object, default: null },
  // A contact's account: its addresses can be pointed at, and stand in where the contact has none.
  accountUuid: { type: String, default: null },
  disabled: Boolean,
  errors: { type: Object, default: () => ({}) },
})
const emit = defineEmits(['update:modelValue'])
const meta = useMeta()
const { t } = useI18n()
const roles = computed(() => addressRoles(meta))
const roleLabel = (key) => roles.value.find((r) => r.key === key)?.label || key
// Short, where several stand side by side: "Centrala", not "Centrala (adres faktyczny)".
const shortRole = (key) => roleLabel(key).replace(/\s*\(.*\)\s*$/, '')
const list = computed(() => props.modelValue || [])
const resolved = computed(() => resolveRoles(list.value, roles.value))
// A role not ticked here, hovered: which address it takes instead (if this one, through what).
function roleHint(i, key) {
  const r = resolved.value[key]
  if (!r || r.via === null || (list.value[i].roles || []).includes(key)) return undefined
  const through = r.via === '*' ? (list.value.length === 1 ? t('addresses.viaOnly') : t('addresses.viaFirst')) : t('addresses.viaRole', { role: shortRole(r.via) })
  return r.index === i ? t('addresses.takenHere', { through }) : t('addresses.takenElsewhere', { through })
}

function write(next) {
  emit('update:modelValue', next)
}
function update(i, patch) {
  write(list.value.map((a, j) => (j === i ? { ...a, ...patch } : a)))
}
function setValues(i, values) {
  const parts = values || {}
  update(i, { street: parts.street ?? null, building: parts.building ?? null, postal_code: parts.postal_code ?? null, city: parts.city ?? null, region: parts.region ?? null, country: parts.country ?? null })
}
function toggle(i, role) {
  write(toggleRole(list.value, i, role, roles.value))
}
// A new address plays nothing until a role is ticked: the first one plays every role through the stand-ins.
function add() {
  write([...list.value, { uuid: newUuid(), roles: [], label: null, street: null, building: null, postal_code: null, city: null, region: null, country: null, linked_address_uuid: null }])
}
function remove(i) {
  write(list.value.filter((_, j) => j !== i))
}

// A contact's account, read once it is known: its addresses to point at.
const account = ref(null)
watch(() => (props.entity === 'contact' ? props.accountUuid : null), async (uuid) => {
  account.value = null
  if (!uuid) return
  try {
    account.value = (await getRecord('account', uuid)).data
  } catch {
    // offline and not kept on the device: nothing to point at
  }
}, { immediate: true })
const accountAddresses = computed(() => account.value?.addresses || [])
const linkedTo = (a) => accountAddresses.value.find((x) => x.uuid === a.linked_address_uuid) || props.record?.addresses?.find((x) => x.linked?.address_uuid === a.linked_address_uuid)?.linked || null
const linkedAccount = (a) => (account.value && accountAddresses.value.some((x) => x.uuid === a.linked_address_uuid) ? { uuid: account.value.uuid, label: account.value.label || account.value.name } : props.record?.addresses?.find((x) => x.linked?.address_uuid === a.linked_address_uuid)?.linked?.account || null)
function linkAccountAddress(uuid) {
  const source = accountAddresses.value.find((x) => x.uuid === uuid)
  if (!source) return
  write([...list.value, { uuid: newUuid(), roles: [], label: null, street: source.street, building: source.building, postal_code: source.postal_code, city: source.city, region: source.region, country: source.country, linked_address_uuid: uuid }])
}
function unlink(i) {
  update(i, { linked_address_uuid: null })
}
const accountLine = (a) => [(a.roles || []).map(roleLabel).join(', '), formatAddress(a)].filter(Boolean).join(' — ')
</script>

<template>
  <div class="space-y-3" data-testid="addresses">
    <p v-if="entity === 'contact' && !list.length && account" class="text-sm text-muted" data-testid="addresses-from-account">
      <i class="fa-solid fa-building mr-1" aria-hidden="true"></i>{{ $t('addresses.fromAccount', { account: account.label || account.name }) }}
      <span v-if="account.address" class="text-faint"> {{ formatAddress(account.address) }}</span>
    </p>
    <div v-for="(a, i) in list" :key="a.uuid || i" class="rounded-lg border border-default p-3" :data-testid="`address-card-${i}`">
      <div class="mb-2 flex flex-wrap items-center gap-1.5">
        <button v-for="r in roles" :key="r.key" type="button" :disabled="disabled" :data-testid="`address-role-${i}-${r.key}`" :aria-pressed="(a.roles || []).includes(r.key)" :title="roleHint(i, r.key)"
          :class="['rounded-full border px-2 py-0.5 text-xs', (a.roles || []).includes(r.key) ? 'border-brand-500 bg-brand-50 font-medium text-brand-800 dark:bg-brand-900/30 dark:text-brand-100' : 'border-default text-muted hover:bg-[var(--surface-3)]']"
          @click="toggle(i, r.key)">
          {{ r.label }}
        </button>
      </div>
      <div class="mb-2 flex items-center gap-2">
        <input class="input min-w-0 flex-1 py-1 text-sm" :value="a.label || ''" :placeholder="$t('addresses.labelPlaceholder')" :disabled="disabled" :data-testid="`address-label-${i}`" @input="update(i, { label: $event.target.value || null })" />
        <button v-if="!disabled" type="button" class="rounded p-1.5 text-faint hover:text-red-600" :aria-label="$t('common.remove')" :data-testid="`address-remove-${i}`" @click="remove(i)"><i class="fa-solid fa-trash-can" aria-hidden="true"></i></button>
      </div>
      <div v-if="a.linked_address_uuid" class="flex flex-wrap items-center gap-2 rounded-md bg-[var(--surface-2)] px-3 py-2 text-sm" :data-testid="`address-linked-${i}`">
        <i class="fa-solid fa-link text-faint" aria-hidden="true"></i>
        <span>{{ formatAddress(a) }}</span>
        <router-link v-if="linkedAccount(a)" class="link" :to="{ name: 'record', params: { entityPath: 'accounts', uuid: linkedAccount(a).uuid } }">
          {{ $t('addresses.ofAccount', { account: linkedAccount(a).label, roles: (linkedTo(a)?.roles || []).map(roleLabel).join(', ') || $t('addresses.noRoles') }) }} <i class="fa-solid fa-arrow-up-right-from-square text-xs" aria-hidden="true"></i>
        </router-link>
        <span class="text-xs text-faint">{{ $t('addresses.followsAccount') }}</span>
        <button v-if="!disabled" type="button" class="link ml-auto text-xs" :data-testid="`address-unlink-${i}`" @click="unlink(i)">{{ $t('addresses.unlink') }}</button>
      </div>
      <AddressInput v-else :model-value="a" :disabled="disabled" @update:model-value="(v) => setValues(i, v)" />
      <p v-if="errors[`addresses.${i}.roles`] || errors[`addresses.${i}.linked_address_uuid`]" class="mt-1 text-xs text-red-600">{{ errors[`addresses.${i}.roles`] || errors[`addresses.${i}.linked_address_uuid`] }}</p>
    </div>
    <div v-if="!disabled" class="flex flex-wrap items-center gap-3">
      <button type="button" class="link text-sm" data-testid="addresses-add" @click="add()"><i class="fa-solid fa-plus text-xs" aria-hidden="true"></i> {{ list.length ? $t('addresses.addAnother') : $t('addresses.add') }}</button>
      <select v-if="entity === 'contact' && accountAddresses.length" class="input w-auto py-1 text-sm" data-testid="addresses-add-linked" :aria-label="$t('addresses.pointAt')" @change="linkAccountAddress($event.target.value); $event.target.value = ''">
        <option value="">{{ $t('addresses.pointAt') }}</option>
        <option v-for="x in accountAddresses" :key="x.uuid" :value="x.uuid">{{ accountLine(x) }}</option>
      </select>
    </div>
  </div>
</template>
